import { readIAiProcessEnv } from "./platform-url-env";
import {
  type IAiPlatformServiceKey,
  readIAiMountedApiEndpoint,
  readIAiPlatformEndpoint,
  readIAiPublicInfrastructureEndpoint,
  readIAiPublicServiceEndpoint,
  requireIAiKnownPlatformService,
} from "./platform-url-registry";
import { resolvePlatformRootDomain } from "./platform-url-root-domain";
import {
  isLocalDevelopmentHost,
  joinIAiPublicServicePath,
  normalizeHostname,
  resolveEffectiveHostname,
  trimTrailingSlash,
} from "./platform-url-utils";

export type IAiBrowserPublicServiceEnvReader = (key: string) => string | null | undefined;

export type IAiBrowserPublicServiceUrlOptions = {
  deprecatedEnvKeys?: readonly string[];
  hostname?: string | undefined;
  origin?: string | undefined;
  readClientEnv?: IAiBrowserPublicServiceEnvReader;
};

export function normalizeIAiPublicServiceRootUrl(
  service: IAiPlatformServiceKey,
  envKey: string,
  rawValue: string | null | undefined,
) {
  const normalizedValue = trimTrailingSlash(String(rawValue || "").trim());
  if (!normalizedValue) {
    return "";
  }
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(normalizedValue);
  } catch {
    throw new Error(
      `${envKey} for ${service} must be an absolute service root URL generated from deploy/service-endpoints.json.`,
    );
  }
  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    throw new Error(
      `${envKey} for ${service} must use http or https service root URLs generated from deploy/service-endpoints.json.`,
    );
  }
  if (parsedUrl.username || parsedUrl.password || parsedUrl.search || parsedUrl.hash) {
    throw new Error(
      `${envKey} for ${service} must be a clean service root URL without credentials, query, or fragment.`,
    );
  }
  requireIAiKnownPlatformService(service);
  const normalizedPathname = trimTrailingSlash(parsedUrl.pathname);
  const mountedApiEndpoint = readIAiMountedApiEndpoint(service);
  if (mountedApiEndpoint) {
    if (normalizedPathname !== mountedApiEndpoint.publicMountPath) {
      throw new Error(
        `${envKey} for ${service} must be the canonical public mount URL ending in ${mountedApiEndpoint.publicMountPath} generated from deploy/service-endpoints.json.`,
      );
    }
    return normalizedValue;
  }
  const publicServiceEndpoint = readIAiPublicServiceEndpoint(service);
  if (publicServiceEndpoint) {
    const allowedPaths = new Set<string>([
      publicServiceEndpoint.domainPath,
      publicServiceEndpoint.ipPath,
    ]);
    if (!allowedPaths.has(normalizedPathname || "/")) {
      throw new Error(
        `${envKey} for ${service} must end in a canonical public path generated from deploy/service-endpoints.json.`,
      );
    }
    return normalizedValue;
  }
  const publicInfrastructureEndpoint = readIAiPublicInfrastructureEndpoint(service);
  if (publicInfrastructureEndpoint && normalizedPathname && normalizedPathname !== "/") {
    throw new Error(
      `${envKey} for ${service} must be the registry-generated public infrastructure root URL without an extra path.`,
    );
  }
  return normalizedValue;
}

function resolveIAiConfiguredServiceUrl(service: IAiPlatformServiceKey) {
  const endpoint = readIAiPlatformEndpoint(service);
  const envKeys = endpoint?.envKeys ?? [];
  for (const key of envKeys) {
    const envValue = readIAiProcessEnv(key);
    if (envValue) {
      return normalizeIAiPublicServiceRootUrl(service, key, envValue);
    }
  }
  return "";
}

export function resolveIAiPlatformServiceUrl(
  service: IAiPlatformServiceKey,
  hostname?: string,
): string {
  const mountedApiEndpoint = readIAiMountedApiEndpoint(service);
  if (mountedApiEndpoint) {
    const hostService = mountedApiEndpoint.publicMountService as IAiPlatformServiceKey;
    requireIAiKnownPlatformService(hostService);
    const hostServiceUrl = resolveIAiPlatformServiceUrl(hostService, hostname);
    return hostServiceUrl
      ? joinIAiPublicServicePath(hostServiceUrl, mountedApiEndpoint.publicMountPath)
      : "";
  }
  const publicServiceEndpoint = readIAiPublicServiceEndpoint(service);
  if (publicServiceEndpoint) {
    const rootDomain = resolvePlatformRootDomain(hostname);
    if (!rootDomain) {
      return "";
    }
    const host =
      publicServiceEndpoint.domainHost === "root"
        ? rootDomain
        : `${publicServiceEndpoint.domainPrefix}.${rootDomain}`;
    return publicServiceEndpoint.domainPath === "/"
      ? `https://${host}`
      : `https://${host}${publicServiceEndpoint.domainPath}`;
  }
  const publicInfrastructureEndpoint = readIAiPublicInfrastructureEndpoint(service);
  if (publicInfrastructureEndpoint) {
    const rootDomain = resolvePlatformRootDomain(hostname);
    if (!rootDomain) {
      return "";
    }
    return `${publicInfrastructureEndpoint.publicDomainScheme}://${publicInfrastructureEndpoint.publicDomainPrefix}.${rootDomain}`;
  }
  requireIAiKnownPlatformService(service);
  return "";
}

function resolveIAiPublicInfrastructureIpUrl(
  service: IAiPlatformServiceKey,
  hostname?: string,
): string {
  const endpoint = readIAiPublicInfrastructureEndpoint(service);
  if (!endpoint) {
    return "";
  }
  const effectiveHostname = resolveEffectiveHostname(hostname);
  if (
    !effectiveHostname ||
    isLocalDevelopmentHost(effectiveHostname) ||
    resolvePlatformRootDomain(effectiveHostname)
  ) {
    return "";
  }
  return `${endpoint.publicIpScheme}://${effectiveHostname}:${endpoint.port}`;
}

function resolveIAiPathPrefixedServiceUrl(
  service: IAiPlatformServiceKey,
  hostname?: string,
): string {
  const configuredPrefix =
    readIAiMountedApiEndpoint(service)?.publicMountPath ??
    readIAiPublicServiceEndpoint(service)?.ipPath;
  if (!configuredPrefix) {
    return resolveIAiPublicInfrastructureIpUrl(service, hostname);
  }
  const effectiveHostname = resolveEffectiveHostname(hostname);
  if (!effectiveHostname || isLocalDevelopmentHost(effectiveHostname)) {
    return "";
  }
  const rootDomain = resolvePlatformRootDomain(effectiveHostname);
  if (rootDomain) {
    return "";
  }
  const browserLocation = typeof window !== "undefined" ? window.location : undefined;
  const browserOrigin = browserLocation?.origin?.replace(/\/+$/, "") ?? "";
  const matchingBrowserOrigin =
    normalizeHostname(browserLocation?.hostname) === effectiveHostname ? browserOrigin : "";
  const origin = matchingBrowserOrigin || `http://${effectiveHostname}`;
  return configuredPrefix === "/" ? `${origin}/` : `${origin}${configuredPrefix}`;
}

function resolveIAiPathPrefixedServiceUrlFromOrigin(
  service: IAiPlatformServiceKey,
  origin?: string,
): string {
  const configuredPrefix =
    readIAiMountedApiEndpoint(service)?.publicMountPath ??
    readIAiPublicServiceEndpoint(service)?.ipPath;
  const normalizedOrigin = trimTrailingSlash(String(origin || "").trim());
  if (!configuredPrefix || !normalizedOrigin) {
    return "";
  }
  return configuredPrefix === "/"
    ? `${normalizedOrigin}/`
    : joinIAiPublicServicePath(normalizedOrigin, configuredPrefix);
}

export function resolveIAiLocalServiceUrl(service: IAiPlatformServiceKey, hostname?: string) {
  const configuredUrl = resolveIAiConfiguredServiceUrl(service);
  if (configuredUrl) {
    return configuredUrl;
  }
  const effectiveHostname = resolveEffectiveHostname(hostname);
  const allowLocalDevelopmentFallback =
    !effectiveHostname || isLocalDevelopmentHost(effectiveHostname);
  if (!allowLocalDevelopmentFallback) {
    return "";
  }
  if (typeof window !== "undefined") {
    const mountedApiEndpoint = readIAiMountedApiEndpoint(service);
    if (mountedApiEndpoint) {
      return joinIAiPublicServicePath(window.location.origin, mountedApiEndpoint.publicMountPath);
    }
  }
  return "";
}

export function resolveIAiDefaultServiceUrl(
  service: IAiPlatformServiceKey,
  hostname?: string,
): string {
  const configuredUrl = resolveIAiConfiguredServiceUrl(service);
  if (configuredUrl) {
    return configuredUrl;
  }
  const platformUrl = resolveIAiPlatformServiceUrl(service, hostname);
  if (platformUrl) {
    return platformUrl;
  }
  const pathPrefixedUrl = resolveIAiPathPrefixedServiceUrl(service, hostname);
  if (pathPrefixedUrl) {
    return pathPrefixedUrl;
  }
  const localUrl = resolveIAiLocalServiceUrl(service, hostname);
  if (localUrl) {
    return localUrl;
  }
  return "";
}

export function resolveIAiBrowserPublicServiceBaseUrl(
  service: IAiPlatformServiceKey,
  clientEnvKey: string,
  options: IAiBrowserPublicServiceUrlOptions = {},
): string {
  const readClientEnv = options.readClientEnv ?? (() => "");
  for (const key of options.deprecatedEnvKeys ?? []) {
    if (String(readClientEnv(key) || "").trim()) {
      throw new Error(
        `${key} is not a valid ${service} browser public service URL env. Use the canonical service env generated from deploy/service-endpoints.json instead.`,
      );
    }
  }
  requireIAiKnownPlatformService(service);
  const endpoint = readIAiPlatformEndpoint(service);
  const clientEnvKeys = Array.from(new Set([clientEnvKey, ...(endpoint?.envKeys ?? [])]));
  for (const envKey of clientEnvKeys) {
    const configured = normalizeIAiPublicServiceRootUrl(service, envKey, readClientEnv(envKey));
    if (configured) {
      return configured;
    }
  }
  const browserLocation = typeof window !== "undefined" ? window.location : undefined;
  const hostname = normalizeHostname(options.hostname || browserLocation?.hostname);
  if (isLocalDevelopmentHost(hostname)) {
    const origin = trimTrailingSlash(options.origin || browserLocation?.origin || "");
    const mountedApiEndpoint = readIAiMountedApiEndpoint(service);
    return mountedApiEndpoint && origin
      ? joinIAiPublicServicePath(origin, mountedApiEndpoint.publicMountPath)
      : origin;
  }
  const originPathPrefixedUrl = resolvePlatformRootDomain(hostname)
    ? ""
    : resolveIAiPathPrefixedServiceUrlFromOrigin(service, options.origin);
  if (originPathPrefixedUrl) {
    return normalizeIAiPublicServiceRootUrl(
      service,
      `resolved ${service} public service URL`,
      originPathPrefixedUrl,
    );
  }
  const resolved = resolveIAiDefaultServiceUrl(service, hostname);
  if (!resolved) {
    throw new Error(
      `Cannot resolve ${service} browser public service URL for host ${hostname}. Maintain service endpoints in deploy/service-endpoints.json and generated public env instead of adding module-local fallbacks.`,
    );
  }
  return normalizeIAiPublicServiceRootUrl(
    service,
    `resolved ${service} public service URL`,
    resolved,
  );
}

export function resolveIAiGeneratedBrowserPublicServiceBaseUrl(
  service: IAiPlatformServiceKey,
  options: IAiBrowserPublicServiceUrlOptions = {},
): string {
  const endpoint = readIAiPlatformEndpoint(service);
  requireIAiKnownPlatformService(service);
  const clientEnvKey =
    endpoint?.envKeys.find((key) => key.startsWith("VITE_")) ??
    endpoint?.envKeys[0] ??
    "registry-generated env key";
  return resolveIAiBrowserPublicServiceBaseUrl(service, clientEnvKey, options);
}

export function resolveIAiApiBaseUrl(
  rawValue: string,
  _currentHostname?: string,
  _currentProtocol?: string,
) {
  return trimTrailingSlash(String(rawValue || "").trim());
}
